import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["loading"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import React, { cloneElement, forwardRef, useEffect } from 'react';
import warning from 'tiny-warning';
import theme from '../../theme.config';
import EzTooltip from '../../EzTooltip';
import { domProps } from '../../../utils';
var spin = theme.keyframes({
  '0%': {
    transform: 'rotate(0)'
  },
  '100%': {
    transform: 'rotate(360deg)'
  }
});
var button = theme.css({
  cursor: 'pointer',
  display: 'inline-block',
  outline: 0,
  fontSize: '$button-text',
  fontWeight: '$button-bold',
  fontFamily: '$defaultFont',
  lineHeight: '$button-leading',
  minHeight: '1rem',
  textDecoration: 'none',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  '-webkit-tap-highlight-color': 'transparent',
  variants: {
    disabled: {
      "true": {
        cursor: 'default',
        opacity: '$shadows$opacity45',
        pointerEvents: 'none'
      }
    },
    use: {
      primary: {
        color: '$button-text-primary',
        backgroundColor: '$button-bg-primary',
        borderColor: '$button-border-primary',
        borderRadius: '$button-primary',
        borderWidth: '$button-primary',
        borderStyle: '$button-primary',
        boxShadow: '$button-primary',
        padding: '$button-py $button-px',
        '&:focus': {
          boxShadow: '$button-primary-focus-ring',
          backgroundColor: '$button-bg-primary-focus',
          borderColor: '$button-bg-primary-focus'
        },
        '&:hover': {
          color: '$button-text-primary-hover',
          backgroundColor: '$button-bg-primary-hover',
          borderColor: '$button-border-primary-hover'
        },
        '&:active': {
          color: '$button-text-primary-down',
          backgroundColor: '$button-bg-primary-down',
          borderColor: '$button-border-primary-down'
        }
      },
      secondary: {
        color: '$button-text-secondary',
        backgroundColor: '$button-bg-secondary',
        borderColor: '$button-border-secondary',
        borderRadius: '$button-secondary',
        borderWidth: '$button-secondary',
        borderStyle: '$button-secondary',
        boxShadow: '$button-secondary',
        padding: '$button-py $button-px',
        '&:focus': {
          boxShadow: '$button-secondary-focus-ring',
          backgroundColor: '$button-bg-secondary-focus',
          borderColor: '$button-border-secondary-focus',
          borderWidth: '$button-secondary'
        },
        '&:hover': {
          color: '$button-text-secondary-hover',
          backgroundColor: '$button-bg-secondary-hover',
          borderColor: '$button-border-secondary-hover'
        },
        '&:active': {
          color: '$button-text-secondary-down',
          backgroundColor: '$button-bg-secondary-down',
          borderColor: '$button-border-secondary-down'
        },
        '&:hover&:focus': {
          borderColor: '$button-border-secondary-focus',
          borderWidth: '$button-secondary-focus'
        }
      },
      tertiary: {
        color: '$button-text-tertiary',
        backgroundColor: '$button-bg-tertiary',
        borderColor: '$button-border-tertiary',
        borderRadius: '$button-tertiary',
        borderWidth: '$button-tertiary',
        borderStyle: '$button-tertiary',
        boxShadow: '$button-tertiary',
        lineHeight: '1rem',
        padding: 0,
        '&:focus': {
          outline: 'auto',
          backgroundColor: '$button-bg-tertiary-focus',
          borderColor: '$button-border-tertiary-focus'
        },
        '&:hover': {
          textDecoration: 'underline',
          color: '$button-text-tertiary-hover',
          backgroundColor: '$button-bg-tertiary-hover',
          borderColor: '$button-border-tertiary-hover'
        },
        '&:active': {
          color: '$button-text-tertiary-down',
          backgroundColor: '$button-bg-tertiary-down',
          borderColor: '$button-border-tertiary-down'
        }
      }
    },
    destructive: {
      "true": {}
    },
    loading: {
      "true": {
        position: 'relative',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          borderRadius: '$round',
          height: '$button-spinner',
          width: '$button-spinner',
          margin: '-$button-spinner 0 0 -$button-spinner',
          borderStyle: 'solid',
          borderWidth: '$button-spinner'
        },
        '&::before': {
          borderColor: '$button-spinner-bg'
        },
        '&::after': {
          borderColor: '#fff transparent transparent',
          boxShadow: '$button-spinner',
          animation: "".concat(spin, " 0.6s linear infinite")
        }
      }
    }
  },
  compoundVariants: [{
    use: 'primary',
    destructive: 'true',
    css: {
      color: '$button-text-primary-destructive',
      backgroundColor: '$button-bg-primary-destructive',
      borderColor: '$button-border-primary-destructive',
      '&:focus': {
        backgroundColor: '$button-bg-primary-destructive-focus',
        borderColor: '$button-bg-primary-destructive-focus'
      },
      '&:hover': {
        color: '$button-text-primary-destructive-hover',
        backgroundColor: '$button-bg-primary-destructive-hover',
        borderColor: '$button-border-primary-destructive-hover'
      },
      '&:active': {
        color: '$button-text-primary-destructive-down',
        backgroundColor: '$button-bg-primary-destructive-down',
        borderColor: '$button-border-primary-destructive-down'
      }
    }
  }, {
    use: 'secondary',
    destructive: 'true',
    css: {
      color: '$button-text-secondary-destructive',
      backgroundColor: '$button-bg-secondary-destructive',
      borderColor: '$button-border-secondary-destructive',
      '&:focus': {
        backgroundColor: '$button-bg-secondary-destructive-focus',
        borderColor: '$button-bg-secondary-destructive-focus'
      },
      '&:hover': {
        color: '$button-text-secondary-destructive-hover',
        backgroundColor: '$button-bg-secondary-destructive-hover',
        borderColor: '$button-border-secondary-destructive-hover'
      },
      '&:active': {
        color: '$button-text-secondary-destructive-down',
        backgroundColor: '$button-bg-secondary-destructive-down',
        borderColor: '$button-border-secondary-destructive-down'
      }
    }
  }, {
    use: 'tertiary',
    destructive: 'true',
    css: {
      color: '$button-text-tertiary-destructive',
      backgroundColor: '$button-bg-tertiary-destructive',
      borderColor: '$button-border-tertiary-destructive',
      '&:focus': {
        backgroundColor: '$button-bg-tertiary-destructive-focus',
        borderColor: '$button-bg-tertiary-destructive-focus'
      },
      '&:hover': {
        color: '$button-text-tertiary-destructive-hover',
        backgroundColor: '$button-bg-tertiary-destructive-hover',
        borderColor: '$button-border-tertiary-destructive-hover'
      },
      '&:active': {
        color: '$button-text-tertiary-destructive-down',
        backgroundColor: '$button-bg-tertiary-destructive-down',
        borderColor: '$button-border-tertiary-destructive-down'
      }
    }
  }, {
    use: 'primary',
    loading: 'true',
    css: {
      color: 'transparent'
    }
  }]
});
var icon = theme.css({
  display: 'flex',
  padding: 2,
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    width: '1em',
    height: '1em',
    marginLeft: 2,
    fontSize: '16px'
  },
  '> * + *': {
    marginLeft: 8
  }
});
var disabledButtonWrapper = theme.css({
  display: 'inline-block'
});
var EzButtonLegacy = forwardRef(function (_ref, ref) {
  var loading = _ref.loading,
    initProps = _objectWithoutProperties(_ref, _excluded);
  useEffect(function () {
    process.env.NODE_ENV !== "production" ? warning(false, '*Recipe Deprecation*. A deprecated legacy version of EzButton is being used. Please use the new EzButton.') : void 0;
  }, []);
  var props = _objectSpread(_objectSpread({}, initProps), {}, {
    use: initProps.use || 'secondary',
    disabled: initProps.disabled || loading
  });
  var additionalProps = domProps(props);
  var buttonElement = React.createElement("button", _extends({
    type: "button"
  }, additionalProps, {
    className: button(_objectSpread(_objectSpread({}, props), {}, {
      loading: loading
    })),
    ref: ref
  }), props.use === 'tertiary' && props.icon ? React.createElement("span", {
    className: icon()
  }, cloneElement(props.icon, {
    'aria-hidden': true,
    focusable: false
  }), props.children && React.createElement("span", null, props.children)) : props.children);
  if (props.disabled && props.disabledMessage) {
    buttonElement = React.createElement(EzTooltip, {
      message: props.disabledMessage
    }, React.createElement("div", {
      className: disabledButtonWrapper()
    }, buttonElement));
  }
  return React.createElement(React.Fragment, null, buttonElement);
});
EzButtonLegacy.displayName = 'EzButtonLegacy';
export default EzButtonLegacy;